import type { MarketingTask } from "@/shared/api/generated/types/marketing"
import {
	MarketingTaskStatus,
	MarketingTaskType,
} from "@/shared/api/generated/types/marketing"

const defaultChannels = [
	{ type: "whatsapp" as const, order: 1, enabled: true },
	{ type: "telegram" as const, order: 2, enabled: true },
	{ type: "sms" as const, order: 3, enabled: true },
]

export function createInitialMarketingTasks(): MarketingTask[] {
	const now = "2026-06-01T10:00:00.000Z"

	return [
		{
			id: "task-mailing-birthday",
			type: MarketingTaskType.mailing,
			name: "Скидки именинникам",
			description: "Скидка 20% на косметолога, уведомление со ссылкой",
			active: true,
			status: MarketingTaskStatus.active,
			schedule: {
				mode: "recurring",
				recurring: { frequency: "day", time: "10:00" },
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: {
				rules: [
					{
						name: "client_birthday",
						operator: "birthday_eq",
						value: { days: 0 },
					},
				],
			},
			exclusion: { enabled: false },
			message: {
				text: "С днём рождения, {имя}! Скидка 20% на косметолога.",
				channels: defaultChannels,
				priority: "medium",
				smsIntegrationId: "default",
			},
			nextRunAt: "2026-06-24T07:00:00.000Z",
			lastRunAt: "2026-05-28T07:02:00.000Z",
			scheduleLabel: "Каждый день в 10:00",
			createdAt: now,
			updatedAt: now,
		},
		{
			id: "task-mailing-recall",
			type: MarketingTaskType.mailing,
			name: "Возврат «спящих» 90+",
			description: "Не были 90+ дней — приглашение на осмотр",
			active: true,
			status: MarketingTaskStatus.active,
			schedule: {
				mode: "recurring",
				recurring: { frequency: "week", time: "11:00", weekday: 1 },
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: {
				rules: [
					{
						name: "last_visit",
						operator: "days_ago_gte",
						value: 90,
					},
				],
			},
			exclusion: { enabled: true, windowDays: 30 },
			message: {
				text: "{имя}, давно не виделись! Запишитесь на осмотр.",
				channels: defaultChannels,
				priority: "medium",
			},
			nextRunAt: "2026-06-24T08:00:00.000Z",
			lastRunAt: "2026-05-25T08:00:00.000Z",
			scheduleLabel: "Каждую неделю в 11:00",
			createdAt: now,
			updatedAt: now,
		},
		{
			id: "task-mailing-promo",
			type: MarketingTaskType.mailing,
			name: "Акция «Чистка лица»",
			description: "Весеннее предложение −30%",
			active: true,
			status: MarketingTaskStatus.active,
			schedule: {
				mode: "once",
				once: { at: "2026-06-05T11:00:00.000Z" },
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: { rules: [] },
			message: {
				text: "Акция −30% на чистку лица до конца месяца!",
				channels: defaultChannels,
				priority: "high",
			},
			nextRunAt: "2026-06-05T11:00:00.000Z",
			lastRunAt: null,
			scheduleLabel: "Разово, по дате",
			createdAt: now,
			updatedAt: now,
		},
		{
			id: "task-mailing-draft",
			type: MarketingTaskType.mailing,
			name: "Новогодняя акция (черновик)",
			description: "Текст и сегмент ещё не настроены",
			active: false,
			status: MarketingTaskStatus.draft,
			schedule: {
				mode: "once",
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: { rules: [] },
			message: {
				text: "",
				channels: defaultChannels,
				priority: "medium",
			},
			nextRunAt: null,
			lastRunAt: null,
			scheduleLabel: "Черновик",
			createdAt: now,
			updatedAt: now,
		},
		{
			id: "task-mailing-reminder",
			type: MarketingTaskType.mailing,
			name: "Напоминание о записи",
			description: "Разовая отправка по филиалу «Центр»",
			active: true,
			status: MarketingTaskStatus.active,
			schedule: {
				mode: "once",
				once: { at: "2026-05-21T11:30:00.000Z" },
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: { rules: [] },
			message: {
				text: "Напоминаем о вашей записи завтра.",
				channels: defaultChannels,
				priority: "low",
			},
			nextRunAt: null,
			lastRunAt: "2026-05-21T11:30:00.000Z",
			scheduleLabel: "Разово, вручную",
			createdAt: now,
			updatedAt: now,
		},
		{
			id: "task-mailing-welcome",
			type: MarketingTaskType.mailing,
			name: "Новые пациенты — приветствие",
			description: "Приветственное сообщение после первой записи",
			active: true,
			status: MarketingTaskStatus.active,
			schedule: {
				mode: "recurring",
				recurring: { frequency: "day", time: "09:15" },
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: {
				rules: [
					{
						name: "first_visit",
						operator: "days_ago_lte",
						value: 1,
					},
				],
			},
			exclusion: { enabled: false },
			message: {
				text: "Добро пожаловать, {имя}! Рады видеть вас в нашей клинике.",
				channels: defaultChannels,
				priority: "medium",
			},
			nextRunAt: "2026-06-24T06:15:00.000Z",
			lastRunAt: "2026-05-18T06:15:00.000Z",
			scheduleLabel: "Каждый день в 09:15",
			createdAt: now,
			updatedAt: now,
		},
		{
			id: "task-report-weekly",
			type: MarketingTaskType.report,
			name: "Еженедельный отчёт по филиалам",
			description: "Выгрузка в e-mail с архивом",
			active: false,
			status: MarketingTaskStatus.paused,
			schedule: {
				mode: "recurring",
				recurring: { frequency: "week", time: "08:00", weekday: 1 },
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: { rules: [] },
			report: {
				fields: [
					{ key: "fio", order: 1 },
					{ key: "phone", order: 2 },
					{ key: "last_doctor", order: 3 },
					{ key: "last_visit_date", order: 4 },
				],
				email: { enabled: true, recipient: "reports@clinic.ru" },
			},
			nextRunAt: "2026-06-30T05:00:00.000Z",
			lastRunAt: "2026-05-20T05:00:00.000Z",
			scheduleLabel: "Каждую неделю",
			createdAt: now,
			updatedAt: now,
		},
		{
			id: "task-report-birthdays",
			type: MarketingTaskType.report,
			name: "Список именинников недели",
			description: "Разовая выгрузка для администратора",
			active: true,
			status: MarketingTaskStatus.active,
			schedule: {
				mode: "once",
				once: { at: "2026-05-28T06:40:00.000Z" },
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: { rules: [] },
			report: {
				fields: [
					{ key: "fio", order: 1 },
					{ key: "birthday", order: 2 },
					{ key: "phone", order: 3 },
				],
			},
			nextRunAt: null,
			lastRunAt: "2026-05-28T06:40:00.000Z",
			scheduleLabel: "Разовая",
			createdAt: now,
			updatedAt: now,
		},
		{
			id: "task-report-scheduled",
			type: MarketingTaskType.report,
			name: "Активные пациенты за месяц",
			description: "Плановая выгрузка на e-mail администратору",
			active: true,
			status: MarketingTaskStatus.active,
			schedule: {
				mode: "once",
				once: { at: "2026-07-01T07:00:00.000Z" },
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: { rules: [] },
			report: {
				fields: [
					{ key: "fio", order: 1 },
					{ key: "phone", order: 2 },
					{ key: "last_visit_date", order: 3 },
				],
				email: { enabled: true, recipient: "admin@clinic.ru" },
			},
			nextRunAt: "2026-07-01T07:00:00.000Z",
			lastRunAt: null,
			scheduleLabel: "Разовая, по дате",
			createdAt: now,
			updatedAt: now,
		},
		{
			id: "task-report-draft",
			type: MarketingTaskType.report,
			name: "VIP-сегмент (черновик)",
			description: "Поля и фильтры ещё не настроены",
			active: false,
			status: MarketingTaskStatus.draft,
			schedule: {
				mode: "once",
				validity: { kind: "forever" },
				timezone: "Europe/Moscow",
			},
			audience: { rules: [] },
			report: {
				fields: [{ key: "fio", order: 1 }],
			},
			nextRunAt: null,
			lastRunAt: null,
			scheduleLabel: "Черновик",
			createdAt: now,
			updatedAt: now,
		},
	]
}
